import * as React from 'react'
import { Link } from 'react-router-dom'
import { ArrowRight } from 'lucide-react'
import { toast } from 'sonner'
import DashboardCard from '@/pages/dashboard/DashboardCard'
import { Skeleton } from '@/components/ui/skeleton'
import { Badge } from '@/components/ui/Badge'
import { Button } from '@/components/ui/button'
import useGetTodoSummary from '@/services/api/cash-flow/useGetTodoSummary'
import useConfirmTodo from '@/services/api/to-do/useConfirmTodo'
import { formatMoneyExact } from '@/pages/dashboard/moneyFormat'
import { dueLabel, moreCount, type TodoItem } from '@/pages/dashboard/todoMath'
import { useFeaturesLoaded, useHasFeature } from '@/components/feature-gate'
import TodoLockedCard from '@/pages/dashboard/widgets/TodoLockedCard'

const CARD_LIMIT = 8

interface TodoRowData extends TodoItem {
  id: number
  amount: number
  source_vault: { id: number; name: string }
  destination_vault: { id: number; name: string }
}

function TodoRow({ item }: { item: TodoRowData }) {
  const { action, status } = useConfirmTodo({ id: item.id })

  const confirm = async () => {
    try {
      await action()
    } catch {
      toast.error(`Couldn't confirm the transfer to ${item.destination_vault.name}.`)
    }
  }

  return (
    <div className="flex items-center gap-3 border-b border-light pb-3 last:border-0 last:pb-0">
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <div className="flex items-center gap-1.5 text-sm text-txt-primary">
          <span className="truncate">{item.source_vault.name}</span>
          <ArrowRight className="size-3.5 shrink-0 text-txt-secondary" />
          <span className="truncate">{item.destination_vault.name}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold tabular-nums text-txt-primary">
            {formatMoneyExact(item.amount)}
          </span>
          <span
            className={`text-[11px] ${item.is_overdue ? 'font-medium text-[hsl(356,72%,47%)]' : 'text-txt-secondary'}`}
          >
            {dueLabel(item)}
          </span>
        </div>
      </div>
      <Button
        size="sm"
        variant="outline"
        loading={status.isPending}
        onClick={confirm}
        className="shrink-0"
      >
        Transfer
      </Button>
    </div>
  )
}

export default function TodoCard() {
  const hasTodoFeature = useHasFeature('to-do')
  const featuresLoaded = useFeaturesLoaded()
  const { data, status } = useGetTodoSummary({
    limit: CARD_LIMIT,
    enabled: featuresLoaded && hasTodoFeature,
  })

  const rows: TodoRowData[] = data?.data ?? []
  const total = data?.meta?.total ?? 0
  const more = moreCount(total, rows.length)

  // Only once the catalogue has actually loaded — useHasFeature answers
  // optimistically while it's in flight, and flashing a locked card at an
  // entitled team on every navigation is worse than a moment of skeleton.
  if (featuresLoaded && !hasTodoFeature) {
    return <TodoLockedCard />
  }

  return (
    <DashboardCard
        title={
          <span className="flex items-center gap-2">
            To do
            {status === 'success' && total > 0 && (
              <Badge variant={rows.some((r) => r.is_overdue) ? 'destructive' : 'secondary'}>
                {total}
              </Badge>
            )}
          </span>
        }
      >
        {status === 'pending' && (
          <div className="flex flex-col gap-3">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="flex items-center gap-3">
                <div className="flex flex-1 flex-col gap-1.5">
                  <Skeleton className="h-3.5 w-3/4" />
                  <Skeleton className="h-3 w-1/2" />
                </div>
                <Skeleton className="h-7 w-16 shrink-0" />
              </div>
            ))}
          </div>
        )}

        {status === 'error' && (
          <div className="flex h-32 items-center justify-center text-sm text-txt-secondary">
            Couldn&apos;t load the to do list.
          </div>
        )}

        {status === 'success' && rows.length === 0 && (
          <div className="flex h-32 flex-col items-center justify-center gap-1 text-center text-sm text-txt-secondary">
            <div className="font-medium text-[hsl(137,57%,41%)]">All caught up</div>
            <div className="text-xs">No transfers due right now.</div>
          </div>
        )}

        {status === 'success' && rows.length > 0 && (
          <>
            <div className="flex flex-col gap-3">
              {rows.map((item) => (
                <TodoRow key={item.id} item={item} />
              ))}
            </div>

            <Link
              to="/my-todos"
              className="mt-1 self-start text-xs font-semibold text-txt-accent"
            >
              View all in My Todos{more > 0 ? ` — ${total} total` : ` (${total})`}
            </Link>
          </>
        )}
    </DashboardCard>
  )
}
